import type { AbilityLevelState } from "@sins2-atlas/compute";
import type { GrantedAbility, UnitAbility } from "@sins2-atlas/game-data";
import { clsx } from "clsx";
import { techKeyLabel } from "../lib/tech-index";
import { subjectKey } from "../lib/tech-set";
import { ABILITY_LEVEL_WORDS } from "../lib/vocabulary";

// The ability's numeric half (Issue 15), shared by the entity page's Abilities section and
// the item shop's granted-ability line so both state the same numbers the same way.
//
// Everything here is already composed by extraction — the game's own labels, figures and
// headings. This renders; it never computes. A leveled ability's values read as a " / "
// progression, so the level caption says what the positions mean — and, since Issue 51,
// which of them is in force. Two levers can decide that: the hull's own Level, and (Issue 54)
// the reader's Tech Set on the 47 abilities the game levels by research. Nothing is ever
// dropped: the list stays the complete reference it has always been and the reader's
// position in it is marked, exactly as the Corruption card renders every tier and
// highlights the active one.

/** "Levels 1 / 2 / 3" — the key to reading every progression below it, with the Ability Level
 * in force marked. Nothing is marked on an unspent ability or a locked one: there is no
 * position the ship is at (ADR 0015 decision 5). Single-level abilities need no caption:
 * their figures carry no slashes, so there is no position to explain and none to mark. */
function LevelCaption({ levelCount, level }: { levelCount: number; level?: AbilityLevelState }) {
  if (levelCount <= 1) return null;
  const inForce = level?.state === "in_force" ? level.level : undefined;
  return (
    <span className="font-mono text-[11.5px] text-muted-2 [font-variant-numeric:tabular-nums]">
      Levels{" "}
      {Array.from({ length: levelCount }, (_, i) => i + 1).map((position, index) => (
        <span key={position}>
          {index > 0 && " / "}
          <span
            className={clsx(position === inForce && "font-semibold text-ink")}
            aria-current={position === inForce ? "true" : undefined}
            title={position === inForce ? ABILITY_LEVEL_WORDS.inForceTitle : undefined}
          >
            {position}
          </span>
        </span>
      ))}
    </span>
  );
}

export function AbilityEffects({
  ability,
  level,
}: {
  ability: UnitAbility | GrantedAbility;
  /** Where the hull's Level leaves this ability (Issue 51). Absent on an item-granted
   * ability and on a hull with no ladder, where nothing marks and nothing locks. */
  level?: AbilityLevelState;
}) {
  if (ability.facts.length === 0 && ability.effects.length === 0) return null;
  return (
    <div className="mt-1 flex flex-col gap-1">
      {(ability.facts.length > 0 || ability.levelCount > 1) && (
        <span className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 font-mono text-[12px] text-ink-3 [font-variant-numeric:tabular-nums]">
          {ability.facts.map((fact) => (
            <span key={fact}>{fact}</span>
          ))}
          <LevelCaption levelCount={ability.levelCount} level={level} />
        </span>
      )}
      {ability.effects.map((group, index) => (
        <div
          // The group's position IS its identity: the game's line groups are ordered and
          // never reorder, and two groups can share a heading.
          // biome-ignore lint/suspicious/noArrayIndexKey: positional by definition.
          key={`group-${index}`}
          className="flex flex-col gap-0.5"
        >
          {group.header !== undefined && (
            <span className="text-[12.5px] text-muted-2">{group.header}</span>
          )}
          <ul
            className={`flex flex-col gap-0.5 font-mono text-[12.5px] text-ink-2 [font-variant-numeric:tabular-nums] ${
              group.header === undefined ? "" : "border-line-faint border-l pl-2.5"
            }`}
          >
            {group.lines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/** The badge beside an ability's name saying where the reader's selection leaves it: the
 * level a locked ability needs, "Unspent" on one the hull's Level permits and no Ability
 * Point has bought (ADR 0015), or the Ability Level in force on one the reader has bought.
 * Nothing at all on an ability no lever decides, and nothing on a single-level one already in
 * force — its figures carry no progression to place. */
export function AbilityLevelMark({
  level,
  levelCount,
}: {
  level: AbilityLevelState;
  levelCount: number;
}) {
  if (level.state === "not_level_driven") return null;
  if (level.state === "locked") {
    return (
      <span className="rounded border border-line px-1.5 py-px font-mono text-[10.5px] uppercase tracking-[0.06em] text-unverified">
        {ABILITY_LEVEL_WORDS.locked(level.requiredLevel)}
      </span>
    );
  }
  if (level.state === "unspent") {
    return (
      <span className="rounded border border-line px-1.5 py-px font-mono text-[10.5px] uppercase tracking-[0.06em] text-unverified">
        {ABILITY_LEVEL_WORDS.unspent}
      </span>
    );
  }
  if (level.state === "research_locked") {
    // The subject's own name, resolved through the trees the Tech picker reads — the same
    // words a reader would tick, so the badge says what to go and do (Issue 54).
    const names = level.requiredSubjects
      .map((subjectId) => techKeyLabel(subjectKey(subjectId)))
      .filter((name): name is string => name !== undefined);
    return (
      <span className="rounded border border-line px-1.5 py-px font-mono text-[10.5px] uppercase tracking-[0.06em] text-unverified">
        {ABILITY_LEVEL_WORDS.researchLocked(names)}
      </span>
    );
  }
  if (levelCount <= 1) return null;
  return (
    <span className="rounded border border-line-faint bg-raised px-1.5 py-px font-mono text-[10.5px] uppercase tracking-[0.06em] text-ink-3">
      {ABILITY_LEVEL_WORDS.inForce(level.level)}
    </span>
  );
}
